import { Vector3, Curve3, Camera } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { computeTangentFrame, tangentToWorld } from './camera/TangentSpace';
import { Easing } from '../core/Easing';
import type { CameraShakeEffect } from './effects/CameraShakeEffect';

export type CameraType = 'follow-side' | 'follow-spline' | 'fixed' | 'look-at' | 'follow-player';
/** @deprecated Use EasingPreset from core/Easing.ts for new code */
export type TransitionEasing = 'instant' | 'linear' | 'ease-in-out' | 'ease-in-cubic';

export interface FollowSideData {
  offset: Vector3;
  distance: number;
  height: number;
  smoothing: number;
  spline?: Curve3;
}

export interface FollowPlayerData {
    offset: Vector3; // Local space: X=Right, Y=Up, Z=Forward
    lookAtOffset: Vector3; // Local space relative to player
    smoothing: number;
    spline: Curve3;
}

export interface FollowSplineData {
  spline: Curve3;
  lookAheadDistance: number;
  smoothing: number;
}

export interface FixedCameraData {
  position: Vector3;
  target: Vector3;
}

export interface LookAtData {
  position: Vector3;
  followTarget: boolean;
}

export interface CameraSegment {
  id: string;
  startT: number;
  endT: number;
  type: CameraType;
  data: FollowSideData | FollowSplineData | FixedCameraData | LookAtData | FollowPlayerData;
  transitionDuration: number;
  transitionEasing: TransitionEasing;
}

interface CameraTransform {
  position: Vector3;
  target: Vector3;
}

/** Cinematic mode state - suspends normal camera updates */
interface CinematicState {
  /** Position to blend from when exiting cinematic */
  lastCinematicPosition: Vector3;
  /** Target to blend from when exiting cinematic */
  lastCinematicTarget: Vector3;
  /** Blend progress 0-1 when exiting cinematic */
  blendOutProgress: number;
  /** Duration of blend-out in seconds */
  blendOutDuration: number;
}

export class CameraController {
  private segments: CameraSegment[] = [];
  private currentSegment: CameraSegment | null = null;
  private previousSegment: CameraSegment | null = null;
  private transitionProgress: number = 1;
  
  // Smoothing state
  private currentCameraTarget: Vector3 = Vector3.Zero();
  private isInitialized: boolean = false;
  
  // Camera shake effect (optional, injected from FXManager)
  private shakeEffect: CameraShakeEffect | null = null;
  
  // Cinematic mode - when true, normal updates are suspended
  private cinematicMode: boolean = false;
  private cinematicState: CinematicState | null = null;

  constructor(
    private camera: Camera
  ) {}

  /**
   * Set the camera shake effect (called by FXManager after initialization)
   */
  setShakeEffect(effect: CameraShakeEffect | null): void {
    this.shakeEffect = effect;
  }

  /**
   * Register a camera segment
   */
  addSegment(segment: CameraSegment): void {
    this.segments.push(segment);
    // Sort segments by startT
    this.segments.sort((a, b) => a.startT - b.startT);
  }

  /**
   * Update a segment's offset by ID
   */
  updateSegmentOffset(segmentId: string, offset: Vector3): void {
    const segment = this.segments.find(s => s.id === segmentId);
    if (segment && segment.data && 'offset' in segment.data) {
      (segment.data as { offset: Vector3 }).offset = offset;
      logger.camera.log(`📷 Updated segment '${segmentId}' offset to (${offset.x.toFixed(2)}, ${offset.y.toFixed(2)}, ${offset.z.toFixed(2)})`);
    } else {
      logger.camera.warn(`📷 Cannot update offset - segment '${segmentId}' not found or has no offset`);
    }
  }

  /**
   * Set the active camera mode starting from the current time
   * This effectively overrides future segments
   */
  setCameraMode(
      type: CameraType, 
      data: any, 
      currentT: number, 
      transitionDuration: number = 1.0
  ): void {
      // 1. Remove segments that start after currentT
      this.segments = this.segments.filter(s => s.endT <= currentT);
      
      // 2. Cap the current segment at currentT if it exists
      const current = this.findSegmentForT(currentT);
      if (current) {
          current.endT = currentT;
      }

      // 3. Add new segment from currentT to end
      this.addSegment({
          id: `override_${Date.now()}`,
          startT: currentT,
          endT: 1.0,
          type: type,
          data: data,
          transitionDuration: transitionDuration,
          transitionEasing: 'ease-in-out'
      });
      
      logger.camera.log(`🎥 Camera mode switched to ${type} at T=${currentT.toFixed(3)}`);
  }

  /**
   * Enter cinematic mode - suspends normal camera updates
   * The camera will be controlled externally (e.g., by AnimationGroup)
   */
  enterCinematicMode(): void {
    this.cinematicMode = true;
    this.cinematicState = null;
    logger.camera.log('🎬 Camera entering cinematic mode');
  }

  /**
   * Exit cinematic mode and blend back to normal camera behavior
   * @param blendDuration Duration in seconds to blend from cinematic to player camera
   * @param finalPosition Optional final position from the cinematic camera (if not using this.camera)
   * @param finalTarget Optional final target from the cinematic camera
   */
  exitCinematicMode(blendDuration: number = 1.0, finalPosition?: Vector3, finalTarget?: Vector3): void {
    if (!this.cinematicMode) return;
    
    // Use provided final position/target (from cinematic camera) or fall back to main camera
    const blendFromPosition = finalPosition ?? this.camera.position.clone();
    const blendFromTarget = finalTarget ?? (
      'target' in this.camera 
        ? (this.camera as any).target as Vector3 
        : this.currentCameraTarget
    );
    
    this.cinematicState = {
      lastCinematicPosition: blendFromPosition.clone(),
      lastCinematicTarget: blendFromTarget.clone(),
      blendOutProgress: 0,
      blendOutDuration: blendDuration
    };
    
    this.cinematicMode = false;
    logger.camera.log(`🎬 Camera exiting cinematic mode (blend: ${blendDuration}s) from pos: (${blendFromPosition.x.toFixed(2)}, ${blendFromPosition.y.toFixed(2)}, ${blendFromPosition.z.toFixed(2)})`);
  }

  /**
   * Check if camera is in cinematic mode
   */
  isInCinematicMode(): boolean {
    return this.cinematicMode;
  }

  /**
   * Update camera based on player position along gameplay spline
   */
  update(playerT: number, playerPosition: Vector3, deltaTime: number): void {
    // If in cinematic mode, don't update camera (it's controlled externally)
    if (this.cinematicMode) {
      // Debug: Verify cinematic mode is working
      // logger.camera.log(`🎬 CameraController.update() - SKIPPING (cinematicMode=true)`);
      return;
    }
    
    // Debug: Log that we're NOT in cinematic mode when we expected to be
    // logger.camera.log(`⚠️ CameraController.update() - EXECUTING (cinematicMode=${this.cinematicMode})`);

    // Find the active segment
    const segment = this.findSegmentForT(playerT);
    
    if (!segment) return;

    // Check if segment changed
    if (this.currentSegment !== segment) {
      this.previousSegment = this.currentSegment;
      this.currentSegment = segment;
      this.transitionProgress = 0;
    }

    // Update transition
    if (this.transitionProgress < 1 && this.currentSegment) {
      this.transitionProgress = Math.min(
        1,
        this.transitionProgress + deltaTime / this.currentSegment.transitionDuration
      );
    }

    // Calculate camera transforms
    let transform: CameraTransform;

    if (this.transitionProgress < 1 && this.previousSegment) {
      // Blend between previous and current
      const oldTransform = this.executeCamera(this.previousSegment, playerT, playerPosition);
      const newTransform = this.executeCamera(this.currentSegment!, playerT, playerPosition);
      const t = this.easeValue(this.transitionProgress, this.currentSegment!.transitionEasing);
      transform = this.blendTransforms(oldTransform, newTransform, t);
    } else if (this.currentSegment) {
      // Use current segment only
      transform = this.executeCamera(this.currentSegment, playerT, playerPosition);
    } else {
      return;
    }

    // Handle blend-out from cinematic mode
    if (this.cinematicState && this.cinematicState.blendOutProgress < 1) {
      this.cinematicState.blendOutProgress = Math.min(
        1,
        this.cinematicState.blendOutProgress + deltaTime / this.cinematicState.blendOutDuration
      );
      
      const blendT = Easing.easeInOut(this.cinematicState.blendOutProgress);
      transform = {
        position: Vector3.Lerp(this.cinematicState.lastCinematicPosition, transform.position, blendT),
        target: Vector3.Lerp(this.cinematicState.lastCinematicTarget, transform.target, blendT)
      };
      
      if (this.cinematicState.blendOutProgress >= 1) {
        this.cinematicState = null;
        logger.camera.log('🎬 Camera blend-out complete');
      }
    }

    // Apply smoothing (skip during blend-out for smoother transition)
    if (!this.isInitialized) {
        this.camera.position = transform.position.clone();
        this.currentCameraTarget = transform.target.clone();
        this.isInitialized = true;
    } else if (this.cinematicState) {
        // During blend-out, apply transform directly (already smoothed by blend)
        this.camera.position = transform.position;
        this.currentCameraTarget = transform.target;
    } else {
        // Get smoothing factor from current segment data
        let smoothing = 0.1; // Default
        if (this.currentSegment && 'smoothing' in this.currentSegment.data) {
             smoothing = (this.currentSegment.data as any).smoothing || 0.1;
        }
        
        // Frame-rate independent smoothing
        // smoothing = 0.1 means 10% correction per frame at 60fps
        const dtRatio = Math.min(deltaTime * 60, 5.0); // Cap at 5 frames to prevent huge jumps
        const lerpFactor = 1 - Math.pow(1 - smoothing, dtRatio);
        
        this.camera.position = Vector3.Lerp(this.camera.position, transform.position, lerpFactor);
        this.currentCameraTarget = Vector3.Lerp(this.currentCameraTarget, transform.target, lerpFactor);
    }

    // Apply camera shake offset (if active)
    // This is the final step - shake is additive and never overwritten
    let finalPosition = this.camera.position;
    let finalTarget = this.currentCameraTarget;
    
    if (this.shakeEffect && this.shakeEffect.hasActiveEffects()) {
      const posOffset = this.shakeEffect.getPositionOffset();
      const targetOffset = this.shakeEffect.getTargetOffset();
      
      finalPosition = this.camera.position.add(posOffset);
      finalTarget = this.currentCameraTarget.add(targetOffset);
      
      // Apply shaken position
      this.camera.position = finalPosition;
    }

    // Apply to camera target
    if ('setTarget' in this.camera) {
      (this.camera as any).setTarget(finalTarget);
    } else if ('target' in this.camera) {
      (this.camera as any).target = finalTarget;
    }
  }

  /**
   * Find the segment that contains the given t-value
   */
  private findSegmentForT(t: number): CameraSegment | null {
    for (const segment of this.segments) {
      if (t >= segment.startT && t < segment.endT) {
        return segment;
      }
    }
    // Return last segment if past end
    return this.segments[this.segments.length - 1] || null;
  }

  /**
   * Execute camera behavior based on type
   */
  private executeCamera(
    segment: CameraSegment,
    playerT: number,
    playerPosition: Vector3
  ): CameraTransform {
    switch (segment.type) {
      case 'follow-side':
        return this.executeFollowSide(segment.data as FollowSideData, playerPosition);
      case 'follow-spline':
        return this.executeFollowSpline(segment.data as FollowSplineData, playerT, playerPosition);
      case 'fixed':
        return this.executeFixed(segment.data as FixedCameraData);
      case 'look-at':
        return this.executeLookAt(segment.data as LookAtData, playerPosition);
      case 'follow-player':
        return this.executeFollowPlayer(segment.data as FollowPlayerData, playerPosition);
      default:
        return { position: this.camera.position, target: playerPosition };
    }
  }

  /**
   * Follow-side: maintain orthogonal distance from gameplay plane
   */
  private executeFollowSide(data: FollowSideData, playerPosition: Vector3): CameraTransform {
    let position: Vector3;
    
    if (data.spline) {
      // Compute tangent frame at player position
      const frame = computeTangentFrame(data.spline, playerPosition);
      
      // Convert tangent-space offset to world position
      position = tangentToWorld(frame, data.offset);
    } else {
      // Fallback to simple offset
      const offset = data.offset || new Vector3(0, 5, -10);
      position = playerPosition.add(offset);
    }
    
    return {
      position,
      target: playerPosition
    };
  }

  /**
   * Follow-player: Follows player with offset relative to spline tangent
   */
  private executeFollowPlayer(data: FollowPlayerData, playerPosition: Vector3): CameraTransform {
      if (!data.spline) {
          // Fallback if spline is missing
          return { position: this.camera.position, target: playerPosition };
      }

      // Calculate orientation from spline
      const t = this.findTForPosition(data.spline, playerPosition);
      const { tangent } = this.getPointAndTangentAtArcLength(data.spline, t);
      
      // Calculate basis vectors
      // Tangent = Forward (Z)
      // Up = World Up (Y)
      // Right = Cross(Up, Tangent) (X)
      const up = Vector3.Up();
      let right = Vector3.Cross(up, tangent);
      
      // Handle degenerate case: if tangent is nearly parallel to Up, 
      // the cross product will be near-zero. Use world right as fallback.
      if (right.lengthSquared() < 0.001) {
        right = Vector3.Right();
      } else {
        right.normalize();
      }
      
      // Re-orthogonalize Up
      const orthoUp = Vector3.Cross(tangent, right).normalize();

      // Calculate world offset from local offset (X=Right, Y=Up, Z=Forward)
      const worldOffset = right.scale(data.offset.x)
          .add(orthoUp.scale(data.offset.y))
          .add(tangent.scale(data.offset.z));
          
      const position = playerPosition.add(worldOffset);
      
      // Calculate target
      const targetOffset = right.scale(data.lookAtOffset.x)
          .add(orthoUp.scale(data.lookAtOffset.y))
          .add(tangent.scale(data.lookAtOffset.z));
          
      const target = playerPosition.add(targetOffset);

      return { position, target };
  }

  /**
   * Find t-value for a position on spline (Arc Length Parameterized)
   */
  private findTForPosition(spline: Curve3, position: Vector3): number {
    const points = spline.getPoints();
    let closestDistance = Infinity;
    let closestIndex = 0;
    let closestSegmentT = 0;

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i+1];
      const segment = p2.subtract(p1);
      const lenSq = segment.lengthSquared();
      
      // Project point onto segment
      const pointVec = position.subtract(p1);
      const t = Math.max(0, Math.min(1, Vector3.Dot(pointVec, segment) / lenSq));
      
      const closest = p1.add(segment.scale(t));
      const dist = Vector3.Distance(closest, position);
      
      if (dist < closestDistance) {
        closestDistance = dist;
        closestIndex = i;
        closestSegmentT = t;
      }
    }

    // Calculate accumulated length
    let currentLength = 0;
    for (let i = 0; i < closestIndex; i++) {
      currentLength += Vector3.Distance(points[i], points[i+1]);
    }
    currentLength += Vector3.Distance(points[closestIndex], points[closestIndex+1]) * closestSegmentT;

    return currentLength / spline.length();
  }

  private getPointAndTangentAtArcLength(spline: Curve3, t: number): { position: Vector3, tangent: Vector3 } {
    const points = spline.getPoints();
    const totalLength = spline.length();
    const targetLength = t * totalLength;
    
    let currentLength = 0;
    
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i+1];
      const segmentLength = Vector3.Distance(p1, p2);
      
      if (currentLength + segmentLength >= targetLength) {
        const segmentT = (targetLength - currentLength) / segmentLength;
        return {
          position: Vector3.Lerp(p1, p2, segmentT),
          tangent: p2.subtract(p1).normalize()
        };
      }
      currentLength += segmentLength;
    }
    
    // Fallback to end
    const last = points[points.length - 1];
    const prev = points[points.length - 2];
    return {
      position: last,
      tangent: last.subtract(prev).normalize()
    };
  }

  /**
   * Follow-spline: follow pre-defined camera path
   */
  private executeFollowSpline(
    data: FollowSplineData,
    playerT: number,
    playerPosition: Vector3
  ): CameraTransform {
    const { position } = this.getPointAndTangentAtArcLength(data.spline, playerT);
    const target = playerPosition.add(new Vector3(data.lookAheadDistance, 0, 0));

    return { position, target };
  }

  /**
   * Fixed: static camera position and target
   */
  private executeFixed(data: FixedCameraData): CameraTransform {
    return {
      position: data.position,
      target: data.target
    };
  }

  /**
   * Look-at: fixed position, track player
   */
  private executeLookAt(data: LookAtData, playerPosition: Vector3): CameraTransform {
    return {
      position: data.position,
      target: data.followTarget ? playerPosition : data.position.add(Vector3.Forward())
    };
  }

  /**
   * Blend two camera transforms
   */
  private blendTransforms(from: CameraTransform, to: CameraTransform, t: number): CameraTransform {
    return {
      position: Vector3.Lerp(from.position, to.position, t),
      target: Vector3.Lerp(from.target, to.target, t)
    };
  }

  /**
   * Apply easing to transition value
   * Maps legacy TransitionEasing names to unified Easing functions
   */
  private easeValue(t: number, easing: TransitionEasing): number {
    switch (easing) {
      case 'instant':
        return Easing.instant(t);
      case 'linear':
        return Easing.linear(t);
      case 'ease-in-out':
        return Easing.easeInOut(t);
      case 'ease-in-cubic':
        return Easing.easeInCubic(t);
      default:
        return t;
    }
  }

  /**
   * Get current segment
   */
  getCurrentSegment(): CameraSegment | null {
    return this.currentSegment;
  }

  /**
   * Get the camera object
   */
  getCamera(): Camera {
      return this.camera;
  }

  /**
   * Clear all segments
   */
  clearSegments(): void {
    this.segments = [];
    this.currentSegment = null;
    this.previousSegment = null;
  }
}
